import { showNotification } from "@mantine/notifications"
import { useMutation, useQuery } from "@tanstack/react-query"
import { RuleGroupType } from "react-querybuilder"

import {
  BillyContributionAnalysisResult,
  BillyHistoryOfCostsResult,
  BillyResponses,
  Period,
} from "@costory/types/endpoints/billy"
import { Position } from "@costory/types/endpoints/digest"
import { GraphResponses } from "@costory/types/endpoints/graph"
import { Filters } from "@costory/types/filters"
import { BillyContributionAnalysisCategory } from "@costory/types/prisma-client"

import { Dayjs } from "@costory/shared/dayjs"

import {
  apiClient,
  apiClientWithLargeTimeout,
} from "@costory/front/lib/apiClient"

export const useContributionAnalysisAvailable = ({
  previousStartDate,
  previousEndDate,
  startDate,
  endDate,
  exclusionWhereClause,
  costColumn,
  category = BillyContributionAnalysisCategory.CONTRIBUTION_ANALYSIS,
}: {
  previousStartDate: Dayjs
  previousEndDate: Dayjs
  startDate: Dayjs
  endDate: Dayjs
  exclusionWhereClause: RuleGroupType
  costColumn: string
  category: BillyContributionAnalysisCategory
}) => {
  return useQuery({
    queryKey: [
      "contribution-analysis-available",
      previousStartDate,
      previousEndDate,
      startDate,
      endDate,
      exclusionWhereClause,
      costColumn,
      category,
    ],
    queryFn: async () => {
      const response = await apiClient.post<
        BillyResponses.ContributionAnalysisAvailable[]
      >("/billy/contribution-factors-available", {
        previousStartDate: previousStartDate.toDate(),
        previousEndDate: previousEndDate.toDate(),
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
        exclusionWhereClause,
        costColumn,
        category,
      })
      return response.data
    },
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 30,
  })
}

export const useRequestNewSession = () => {
  return useMutation({
    mutationFn: async ({
      position,
      historyOfCostsOutput,
      contributionAnalysisOutput,
      previousPeriod,
      currentPeriod,
    }: {
      position: Position
      historyOfCostsOutput: BillyHistoryOfCostsResult
      contributionAnalysisOutput: BillyContributionAnalysisResult
      previousPeriod: Period
      currentPeriod: Period
    }) => {
      const response = await apiClient.post<BillyResponses.RequestNewSession>(
        "/billy/agent-reporting/request-new-session",
        {
          position,
          historyOfCostsOutput,
          contributionAnalysisOutput,
          previousPeriod,
          currentPeriod,
        },
      )
      return response.data.id
    },
    onSuccess: () => {
      showNotification({
        position: "top-right",
        title: "Session created",
        message: "Session created successfully",
      })
    },
    onError: () => {
      showNotification({
        position: "top-right",
        title: "Error creating session",
        color: "red",
        message: "State is not valid",
      })
    },
  })
}

export const callAgenticSSE = ({
  sessionId,
  prompt,
  onMessage,
  onFinished,
}: {
  sessionId: string
  prompt: string
  onMessage: (message: BillyResponses.RunEvent) => void
  onFinished: () => void
}) => {
  try {
    apiClientWithLargeTimeout
      .get(`/billy/agent-reporting/run/${sessionId}/${prompt}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        responseType: "stream",
        adapter: "fetch",
      })
      .then(async (response) => {
        const stream = response.data

        // consume response
        const reader = stream.pipeThrough(new TextDecoderStream()).getReader()
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          try {
            onMessage(JSON.parse(value))
          } catch (error) {
            // You might want to log the error or handle it otherwise
            console.error("Failed to parse JSON from SSE:", error, value)
          }
        }
        onFinished()
      })
      .catch((error) => {
        console.error("Error calling agentic SSE:", error)
        showNotification({
          title: "Error calling agentic SSE",
          color: "red",
          message: "Error",
        })
        onFinished()
      })
  } catch (error) {
    console.error("Error calling agentic SSE:", error)
    showNotification({
      title: "Error calling agentic SSE",
      color: "red",
      message: "Error",
    })
    onFinished()
  }
}

export const useGetSessionQuery = ({ sessionId }: { sessionId?: string }) => {
  return useQuery({
    queryKey: ["session", sessionId],
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 30,
    queryFn: async () => {
      const response = await apiClient.get<BillyResponses.RunEvent[]>(
        `/billy/agent-reporting/get-session/${sessionId}`,
      )
      return response.data
    },
    enabled: !!sessionId,
  })
}

export const useGetChartImage = ({ chart }: { chart: Filters }) => {
  return useQuery({
    queryKey: ["chart-image", chart],
    queryFn: async () => {
      const response = await apiClient.post<GraphResponses.Url>("/graph/url", {
        filters: chart,
      })
      return response.data
    },
    enabled: !!chart.from && !!chart.to,
  })
}
